/**
 * Shared "the user's session is no longer valid, sign them out" interceptor
 * state. Lives in its own module so both ChiroUpAPI (Amplify path) and
 * Safari16API (fetch fallback on iOS/Safari <=16) can call into the same latch
 * without an import cycle.
 *
 * The handler is fired only when the response body explicitly carries
 * `sessionInvalid: true`. Bare 401/403 is intentionally NOT enough — the
 * codebase uses those codes for in-app authorization (clinic/role checks,
 * channel membership, MFA gate) as well, and signing out for those would
 * wrongly bounce a valid user to the login screen. See SessionInvalidError
 * (libs/core/src/lib/functions/errors.ts) for the backend side.
 */

export type UnauthorizedInfo = {
  statusCode?: number;
  message?: string;
};

let onUnauthorized: ((info: UnauthorizedInfo) => void) | null = null;
let unauthorizedFired = false;

const fireUnauthorized = (info: UnauthorizedInfo) => {
  if (!onUnauthorized || unauthorizedFired) return;
  unauthorizedFired = true;
  // Guard against a handler that throws synchronously: in the get/put/post
  // wrappers the catch block does `reject(buildApiError(err))`, so a sync
  // throw out of here would skip reject() and hang the outer promise — the
  // exact bug class this whole interceptor exists to prevent.
  try {
    onUnauthorized(info);
  } catch (e) {
    // Swallowed so the API call's own rejection still propagates. Logged so
    // a misbehaving handler doesn't disappear into the void.
    console.error('[apiInterceptor] handler threw:', e);
  }
};

/**
 * Inspect a parsed response body. Fires the registered handler only when the
 * backend has explicitly tagged the response with `sessionInvalid: true`.
 */
export const checkSessionInvalid = (
  body: Record<string, any> | null | undefined,
  statusCode?: number
) => {
  if (body?.code === 'LEGAL_ACCEPTANCE_REQUIRED' && typeof window !== 'undefined') {
    window.dispatchEvent(new Event('chiroup:legal-required'));
  }
  if (body?.sessionInvalid === true) {
    // Defensive: a non-string `message` (object, number, etc.) would crash
    // any consumer that does `.slice(...)` or string interpolation on it.
    const message = typeof body?.message === 'string' ? body.message : undefined;
    fireUnauthorized({ statusCode, message });
  }
};

export const setOnUnauthorized = (handler: (info: UnauthorizedInfo) => void) => {
  onUnauthorized = handler;
};

/**
 * Re-arm the latch after a fresh sign-in so the handler can fire again on a
 * subsequent session death. The SPA can complete a sign-out/sign-in cycle
 * without a full page reload, so the latch must be reset on the `signedIn`
 * Hub event (see App.tsx).
 */
export const resetUnauthorized = () => {
  unauthorizedFired = false;
};
